import {makePropsAreEqual} from '@wandb/weave/common/util/shouldUpdate';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import clsx from 'clsx';
import copyToClipboard from 'copy-to-clipboard';
import {isUndefined} from 'lodash';
import React, {memo, useState} from 'react';
// @todo: Migrate to 'Toast' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {toast} from 'react-toastify';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Label} from 'semantic-ui-react';

import {useViewAction} from '../../state/views/hooks';
import * as RunSetActions from '../../state/views/runSet/actions';
import {getSweepDisplayName} from '../../util/getSweepDisplayName';
import {isMobile} from '../../util/panelbankFlow';
import {WBTableRowWithRunSetInfo} from '../../util/tablecols';
import {versionGte} from '../../util/version';
import ActiveRunIndicator from '../ActiveRunIndicator';
import DeleteRunModal from '../DeleteRunModal';
import StopRunModal from '../StopRunModal';
import {useRowPinningState} from './hooks/useRowPinningState';
import {useRunHighlighter} from './hooks/useRunHighlighter';
import {
  RunSelectorRowHeaderProps,
  useRunSelectorRowHeaderProps,
} from './hooks/useRunSelectorRowHeaderProps';
import LaunchJobIcon from './LaunchJobIcon';
import {MoveRunDrawer} from './MoveRunDrawer/MoveRunDrawer';
import {RowBaseline} from './RowHeader/RowBaseline';
import {RowColorPicker} from './RowHeader/RowColorPicker';
import {RowExpandChildren} from './RowHeader/RowExpandChildren';
import {RowPinning} from './RowHeader/RowPinning';
import {RowSelection} from './RowHeader/RowSelection';
import {RowVisibility} from './RowHeader/RowVisibility';
import {RowHeaderLabel} from './RowHeaderLabel';
import {RowHeaderMenu} from './RowHeaderMenu/RowHeaderMenu';
import {RunPendingResultsPopup} from './RunPendingResultsPopup';
import {useRunSelectorAnalyticsLocation} from './RunSelectorAnalyticsLocationContext';
import {useRunSelectorFeatureDisabled} from './RunSelectorFeaturesContext';
import {useCurrentRunSetRef} from './RunSelectorRunSetContext';

function isRunStoppable(row: WBTableRowWithRunSetInfo): boolean {
  // TODO(adrnswanberg): Centralize this with similar logic in RunPageActions.tsx,
  // probably around when we start using the same `Run` type everywhere.
  const cliVersion = row._wandb['value.cli_version'] ?? row._wandb.cli_version;
  if (cliVersion == null || typeof cliVersion !== 'string') {
    return false;
  }

  const isJupyterRun =
    row._wandb['value.is_jupyter_run'] ?? row._wandb.is_jupyter_run ?? false;

  return versionGte(cliVersion, '0.8.6') && !isJupyterRun;
}

function showColorPicker(
  row: WBTableRowWithRunSetInfo,
  isGroup: boolean,
  recursionDepth: number
) {
  const groupCounts = row.groupCounts ?? [];

  const groupCount =
    groupCounts.length >= 2 ? groupCounts[groupCounts.length - 2] : null;

  // Show color picker whenever we're not grouped, or when we're at
  // the deepest grouping level.
  return isGroup ? groupCount == null : recursionDepth === 0;
}

type AllRunSelectorRowHeaderProps = RunSelectorRowHeaderProps &
  ReturnType<typeof useRunSelectorRowHeaderProps>;

const RunSelectorRowHeaderInnerComp: React.FC<AllRunSelectorRowHeaderProps> = ({
  className,
  closeDeleteModal,
  closeStopModal,
  customRunColor,
  customRunName,
  customRunNamesRef,
  deleteModalOpen,
  disableRunLink,
  entityName,
  groupedBy,
  isGroup,
  isGroupExpanded,
  isHoveringRow,
  isLaunchActive,
  isLaunchPublicQueueActive,
  isRunGroup,
  isSingleMode,
  isTableCollapsed,
  loadingChildren,
  onGroupButtonClick,
  onSelectRunName,
  onTempCheckboxClick,
  openDeleteModal,
  openStopModal,
  projectName,
  recursionDepth,
  row,
  runIsInBounds,
  setCustomRunColor,
  setDeleteModalOpen,
  setLaunchDrawerRow,
  showArtifactCounts,
  showLogCounts,
  stopModalOpen,
  stopRun,
  tempCheckboxState,
  tempSelectToggle,
  toggleSelection,
  truncationType,
  uniqueId,
  visCheckboxState,
  groupSelectionsRef,
  selectedCount,
}) => {
  const analyticsLocation = useRunSelectorAnalyticsLocation();
  const [moveRunDrawerOpen, setMoveRunDrawerOpen] = useState(false);
  const isDisabled = useRunSelectorFeatureDisabled();
  const {handleMouseIn, handleMouseOut} = useRunHighlighter(uniqueId);
  const {isRunPinned, isRunBaseline} = useRowPinningState(row);
  const runSetRef = useCurrentRunSetRef();
  const setPinnedRun = useViewAction(runSetRef, RunSetActions.setPinnedRun);
  const setBaselineRun = useViewAction(runSetRef, RunSetActions.setBaselineRun);

  function onCopyRunName() {
    const name = customRunName || row.displayName;
    copyToClipboard(name);
    toast('Copied run name to clipboard');
  }

  function onCopyRunPath() {
    const fullPath = `${entityName}/${projectName}/${row.name}`;
    copyToClipboard(fullPath);
    toast('Copied run path to clipboard');
  }

  const canStopRun =
    row.state === 'running' && (row.sweep != null || isRunStoppable(row));

  const groupCounts = row.groupCounts ?? [];

  let runCount;
  let groupCount;
  if (groupCounts.length >= 2) {
    groupCount = groupCounts[groupCounts.length - 2];
    runCount = groupCounts[groupCounts.length - 1];
  } else if (groupCounts.length === 1) {
    [runCount] = groupCounts;
  }

  const isHeaderDropdownEmpty =
    isDisabled('edit-run-name') &&
    isDisabled('stop-run') &&
    isDisabled('delete');

  const cls = clsx(
    'wb-tree-cell--header-row',
    'wb-tree-cell--name',
    className,
    {
      'wb-tree-cell--loading-children': loadingChildren,
    }
  );

  // Indirection here to trick typescript:
  const twProps = {
    className: cls,
    style: {paddingLeft: recursionDepth * 26},
    onMouseEnter: handleMouseIn,
    onMouseLeave: handleMouseOut,
  };

  return (
    <>
      <MoveRunDrawer
        entityName={entityName}
        onClose={() => setMoveRunDrawerOpen(false)}
        onMovedRuns={() => setMoveRunDrawerOpen(false)}
        open={moveRunDrawerOpen}
        projectName={projectName}
        runs={[row]}
        groupSelectionsRef={groupSelectionsRef}
        selectedCount={selectedCount}
      />
      <DeleteRunModal
        entityName={entityName}
        isRunActive={row.state === 'running'}
        isOpen={deleteModalOpen}
        setIsModalOpen={setDeleteModalOpen}
        closeModal={closeDeleteModal}
        projectName={projectName}
        runDisplayName={row.displayName}
        runId={row.id}
        runName={row.name}
      />
      <StopRunModal
        shouldShowModal={stopModalOpen}
        onModalCanceled={closeStopModal}
        onStopRun={stopRun}
      />
      <RunPendingResultsPopup isPendingUpdates={Boolean(row.pendingUpdates)}>
        <Tailwind {...twProps}>
          <RowSelection
            checkboxState={tempCheckboxState}
            isGroup={Boolean(isGroup)}
            isTableCollapsed={Boolean(isTableCollapsed)}
            onPress={() => {
              tempSelectToggle();
              onTempCheckboxClick();
              window.analytics?.track('Run selected', {
                location: analyticsLocation,
              });
            }}
          />
          <RowPinning
            isRunPinned={Boolean(isRunPinned)}
            isSingleMode={Boolean(isSingleMode)}
            onUnpinRow={() => setPinnedRun(row, false)}
          />
          <RowVisibility
            isHiddenByParallelCoordinatesOrScatterPlot={runIsInBounds === false}
            isRunPinned={Boolean(isRunPinned)}
            isSingleMode={Boolean(isSingleMode)}
            onChangeVisibility={() => {
              toggleSelection(row, recursionDepth + 1);
              window.analytics?.track('Run visibility toggled', {
                location: analyticsLocation,
              });
            }}
            visibilityState={visCheckboxState}
          />
          <RowBaseline
            isRowBaseline={Boolean(isRunBaseline)}
            isSingleMode={Boolean(isSingleMode)}
            onRemoveBaseline={() => {
              setBaselineRun(null);
            }}
          />
          <RowColorPicker
            customRunColor={customRunColor}
            isColorPickerVisible={showColorPicker(
              row,
              Boolean(isGroup),
              recursionDepth
            )}
            isRunBaseline={Boolean(isRunBaseline)}
            onOpenColorPicker={() => {
              window.analytics?.track('Run color dropdown clicked', {
                location: analyticsLocation,
              });
            }}
            onSetCustomRunColor={setCustomRunColor}
          />
          <RowExpandChildren
            isExpanded={isGroupExpanded}
            isGroup={Boolean(isGroup)}
            isLoading={Boolean(loadingChildren)}
            onToggleExpand={onGroupButtonClick}
          />
          <RowHeaderLabel
            customSweepName={
              row?.sweep && row.sweep.name
                ? getSweepDisplayName({
                    ...row.sweep,
                    name: row.sweep.name,
                  })
                : null
            }
            customRunName={customRunName}
            disableRunLink={Boolean(disableRunLink)}
            entityName={entityName}
            groupedBy={groupedBy}
            isGroup={Boolean(isGroup)}
            isHoveringRow={isHoveringRow}
            isRunGroup={isRunGroup}
            isSingleMode={Boolean(isSingleMode)}
            onSelectRunName={name => {
              if (onSelectRunName) {
                onSelectRunName(name);
              }
            }}
            projectName={projectName}
            row={row}
            truncationType={truncationType}
            uniqueId={uniqueId}
          />
          {(isLaunchActive || isLaunchPublicQueueActive) &&
            row.state !== 'running' &&
            !isMobile() && (
              <LaunchJobIcon
                projectName={projectName}
                entityName={entityName}
                row={row}
                setLaunchDrawerRow={setLaunchDrawerRow}
                isLaunchActive={isLaunchActive}
                isLaunchPublicQueueActive={isLaunchPublicQueueActive}
              />
            )}
          {isGroup && (
            <>
              {!isUndefined(groupCount) && (
                <Label className="count-label">{groupCount}</Label>
              )}
              {!isUndefined(runCount) && (
                <Label className="count-label">{runCount}</Label>
              )}
            </>
          )}
          {!isGroup && showArtifactCounts && (
            <>
              {row.inputArtifactsCount !== 0 && (
                <Label className="count-label">
                  In {row.inputArtifactsCount}
                </Label>
              )}
              {row.outputArtifactsCount !== 0 && (
                <Label className="count-label">
                  Out {row.outputArtifactsCount}
                </Label>
              )}
            </>
          )}

          {!isGroup && showLogCounts && row.logLineCount != null && (
            <Label className="count-label">{row.logLineCount}</Label>
          )}

          {row.state === 'running' && <ActiveRunIndicator />}

          <RowHeaderMenu
            canStopRun={canStopRun}
            customRunNamesRef={customRunNamesRef}
            isHeaderDropdownEmpty={Boolean(isHeaderDropdownEmpty)}
            isGroup={Boolean(isGroup)}
            onCopyRunName={onCopyRunName}
            onCopyRunPath={onCopyRunPath}
            onDeleteRun={openDeleteModal}
            onMoveRun={() => setMoveRunDrawerOpen(true)}
            onStopRun={openStopModal}
            row={row}
            visible={isHoveringRow}
          />
        </Tailwind>
      </RunPendingResultsPopup>
    </>
  );
};

const RunSelectorRowHeaderInner = memo(
  RunSelectorRowHeaderInnerComp,
  (prevProps, nextProps) => {
    if (prevProps.row.state !== nextProps.row.state) {
      return false;
    }
    if (prevProps.row.displayName !== nextProps.row.displayName) {
      return false;
    }
    if (prevProps.row.groupCounts !== nextProps.row.groupCounts) {
      return false;
    }
    const propsAreEqual = makePropsAreEqual({
      name: 'RunSelectorRowHeader',
      deep: ['groupKey', 'tempCheckboxState', 'customRunColors'],
      // Can ignore, the row remounts when run id changes. This will screw up polling though
      // We should pass the value in rather than row probably, or maybe just check if our value
      // changes here in shouldUpdate
      ignore: ['row', 'cellHoverProps'],
      ignoreFunctions: true,
      debug: false,
      verbose: true,
    });
    return propsAreEqual(prevProps, nextProps);
  }
);

export const RunSelectorRowHeader = (props: RunSelectorRowHeaderProps) => {
  const reduxProps = useRunSelectorRowHeaderProps(props);
  return <RunSelectorRowHeaderInner {...props} {...reduxProps} />;
};
